import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createRecord, updateRecord } from '../../services/recordStorageService';
import { persistedClientRecords } from '../../services/mockData/clients';
import { getRecordsForObject } from '../../services/recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { TabbedModal } from './TabbedModal';
import { usePageLayout } from '../../hooks/useObjectManager';
import { EditSection } from '../edit/EditSection';
import { SearchableSelect } from './SearchableSelect';
import { useToast } from '../../contexts/ToastContext';
import { ListViewRecord } from '../../types/listView';
import { DollarSign, TrendingUp, FileText, Calendar, Link2 } from 'lucide-react';
import { validateRecord, type ValidationError } from '../../services/queries/validationRuleQueries';
import { ValidationErrors } from '../common/ValidationErrors';
import { RelatedRecordsTab } from '../tabs/RelatedRecordsTab';
import { ActivityTab } from '../tabs/ActivityTab';
import { StageProgressionTab } from '../tabs/StageProgressionTab';
import { getStructuredPicklistValues } from '../../services/mockData/objectManagerFields';

/**
 * Stage to Probability mapping for Regal's sales process (PE-1459)
 * When a stage is selected, the probability auto-updates to this value
 * unless the user manually overrides it.
 */
export const STAGE_PROBABILITY_MAP: Record<string, number> = {
  'Meeting Set': 10,
  'Discovery': 20,
  'Pitch': 40,
  'Evaluation': 60,
  'Pending Deployment': 80,
  'Closed Won': 100,
  'Closed Lost': 0,
};

/**
 * Ordered list of opportunity stages for Regal's sales process
 */
export const OPPORTUNITY_STAGES = [
  'Meeting Set',
  'Discovery',
  'Pitch',
  'Evaluation',
  'Pending Deployment',
  'Closed Won',
  'Closed Lost',
];

interface OpportunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity?: ListViewRecord | null; // If provided, edit mode. If null, create mode
}

export const OpportunityModal: React.FC<OpportunityModalProps> = ({
  isOpen,
  onClose,
  opportunity
}) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  const isEditMode = !!opportunity;

  // Get page layout for this opportunity
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Opportunity__c', opportunity);

  console.log('📝 Opportunity Modal - Page Layout:', {
    opportunityId: opportunity?.id,
    layoutName: pageLayout?.name,
    layoutApplication: pageLayout?.applicationName,
    sectionCount: pageLayout?.sections?.length,
    sections: pageLayout?.sections?.map(s => s.title)
  });

  // Define tabs based on mode
  const tabs = isEditMode
    ? [
        // EDIT MODE: All tabs
        { id: 'overview', label: 'Overview', icon: DollarSign },
        { id: 'related', label: 'Related', icon: Link2 },
        { id: 'notes', label: 'Notes', icon: FileText },
        { id: 'activity', label: 'Activity', icon: Calendar },
        { id: 'pipeline', label: 'Pipeline', icon: TrendingUp }
      ]
    : [
        // CREATE MODE: Overview only
        { id: 'overview', label: 'Overview', icon: DollarSign }
      ];

  const shouldGobodo = shouldUseGobodo();
  const { data: gobodoClients } = useQuery({
    queryKey: ['modalClients'],
    queryFn: () => getRecordsForObject('Client__c'),
    enabled: shouldGobodo && isOpen
  });
  const allClients = (shouldGobodo && gobodoClients) ? gobodoClients : persistedClientRecords.get();

  // Form state - using __c field names to match page layout (PE-1461)
  const [formData, setFormData] = useState<Record<string, any>>({
    Name__c: '',
    Amount__c: '',
    Stage__c: 'Meeting Set',
    Probability__c: '10',
    CloseDate__c: '',
    Owner__c: 'John Smith',
    Type__c: 'New Business',
    LeadSource__c: 'Website',
    ClientId__c: '',
    ClientName__c: '',
    NextStep__c: '',
    Description__c: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // PE-1470: Track if user has manually edited probability (to avoid overwriting their edits)
  const probabilityManuallyEdited = useRef(false);

  // Notes state
  const [notes, setNotes] = useState<string[]>([]);
  const [newNote, setNewNote] = useState('');

  // Populate form in edit mode
  useEffect(() => {
    if (opportunity) {
      setFormData({
        Name__c: (opportunity as any).Name__c as string || '',
        Amount__c: (opportunity as any).Amount__c?.toString() || '',
        Stage__c: (opportunity as any).Stage__c as string || 'Meeting Set',
        Probability__c: (opportunity as any).Probability__c?.toString() || '10',
        CloseDate__c: (opportunity as any).CloseDate__c as string || '',
        Owner__c: (opportunity as any).Owner__c as string || 'John Smith',
        Type__c: (opportunity as any).Type__c as string || 'New Business',
        LeadSource__c: (opportunity as any).LeadSource__c as string || 'Website',
        ClientId__c: (opportunity as any).ClientId__c as string || '',
        ClientName__c: (opportunity as any).ClientName__c as string || '',
        NextStep__c: (opportunity as any).NextStep__c as string || '',
        Description__c: (opportunity as any).Description__c as string || ''
      });
      setNotes((opportunity as any).notes as string[] || []);
    } else {
      // Reset form for create mode
      setFormData({
        Name__c: '',
        Amount__c: '',
        Stage__c: 'Meeting Set',
        Probability__c: '10',
        CloseDate__c: '',
        Owner__c: 'John Smith',
        Type__c: 'New Business',
        LeadSource__c: 'Website',
        ClientId__c: '',
        ClientName__c: '',
        NextStep__c: '',
        Description__c: ''
      });
      setNotes([]);
    }
    setErrors({});
    setNewNote('');
    // PE-1470: Reset manual edit tracking when modal opens
    probabilityManuallyEdited.current = false;
  }, [opportunity, isOpen]);

  // Handle field changes (for dynamic fields)
  const handleFieldChange = (fieldName: string, value: any) => {
    // Special handling for client dropdown
    if (fieldName === 'ClientId__c' || fieldName === 'clientId') {
      const selectedClient = allClients.find(c => c.id === value);
      setFormData(prev => ({
        ...prev,
        ClientId__c: value,
        ClientName__c: selectedClient?.name as string || ''
      }));
    }
    // PE-1470: Auto-fill target field when Stage changes (unless user has manually edited it)
    else if (fieldName === 'Stage__c') {
      // Get structured picklist values with metadata
      const stageValues = getStructuredPicklistValues('Stage__c', 'obj_opportunity');
      const selectedStage = stageValues?.find(pv => pv.value === value);
      const targetField = selectedStage?.metadata?.targetField as string | undefined;
      const defaultValue = selectedStage?.metadata?.defaultValue;

      setFormData(prev => {
        const updates: Record<string, any> = { ...prev, Stage__c: value };

        // Only auto-fill if user hasn't manually edited the target field AND we have a default value
        if (targetField && defaultValue !== undefined && !probabilityManuallyEdited.current) {
          updates[targetField] = defaultValue.toString();
          console.log('[OpportunityModal] PE-1470: Auto-filled', targetField, 'to', defaultValue, 'based on Stage', value);
        }

        return updates;
      });
    }
    // Track manual probability edits
    else if (fieldName === 'Probability__c') {
      probabilityManuallyEdited.current = true;
      setFormData(prev => ({ ...prev, [fieldName]: value }));
    }
    else {
      setFormData(prev => ({ ...prev, [fieldName]: value }));
    }

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Handle change events (legacy support)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      setNotes([...notes, newNote.trim()]);
      setNewNote('');
    }
  };

  const handleRemoveNote = (index: number) => {
    setNotes(notes.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    const name = formData.Name__c || '';
    const clientId = formData.ClientId__c || '';
    const amount = formData.Amount__c || '';
    const probability = formData.Probability__c || '';
    const closeDate = formData.CloseDate__c || '';

    if (!name.trim()) {
      newErrors.Name__c = 'Opportunity name is required';
    }

    if (!clientId) {
      newErrors.ClientId__c = 'Client is required';
    }

    if (amount && isNaN(Number(amount))) {
      newErrors.Amount__c = 'Amount must be a valid number';
    }

    if (probability) {
      const prob = Number(probability);
      if (isNaN(prob) || prob < 0 || prob > 100) {
        newErrors.Probability__c = 'Probability must be between 0 and 100';
      }
    }

    if (!closeDate) {
      newErrors.CloseDate__c = 'Close date is required';
    }

    console.log('[OpportunityModal] Validation:', { name, clientId, amount, probability, closeDate, errors: newErrors });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, any> & { notes: string[] }) => {
      // Data is already in Gobodo field format
      const gobodoData: Record<string, any> = {
        Name__c: data.Name__c,
        Amount__c: data.Amount__c ? parseFloat(String(data.Amount__c)) : null,
        Stage__c: data.Stage__c,
        Probability__c: data.Probability__c ? parseFloat(String(data.Probability__c)) : null,
        CloseDate__c: data.CloseDate__c,
        Owner__c: data.Owner__c,
        Type__c: data.Type__c,
        LeadSource__c: data.LeadSource__c,
        ClientId__c: data.ClientId__c,
        ClientName__c: data.ClientName__c,
        NextStep__c: data.NextStep__c,
        Description__c: data.Description__c,
        LastModifiedDate__c: new Date().toISOString()
      };

      // Remove null/empty values
      Object.keys(gobodoData).forEach(key => {
        if (gobodoData[key] === null || gobodoData[key] === '' || gobodoData[key] === undefined) {
          delete gobodoData[key];
        }
      });

      // Ensure numeric fields are stored as numbers
      const recordData = {
        ...data,
        Amount__c: data.Amount__c ? Number(data.Amount__c) : 0,
        Probability__c: data.Probability__c ? Number(data.Probability__c) : 0,
      };

      if (isEditMode && opportunity) {
        const updated = await updateRecord('Opportunity__c', String(opportunity.id), gobodoData);
        if (!updated) throw new Error('Failed to update opportunity');
        return { ...opportunity, ...data, id: updated.id };
      } else {
        const created = await createRecord('Opportunity__c', gobodoData);
        return { ...data, id: created.id };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['opportunityListViews'] });
      queryClient.invalidateQueries({ queryKey: ['opportunityRecords'] });
      queryClient.invalidateQueries({ queryKey: ['opportunityListViewData'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Opportunity__c'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Opportunity'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['opportunity', opportunity?.id] });
      }

      const oppName = (data as any).Name__c || 'Opportunity';
      success(
        isEditMode ? 'Opportunity updated' : 'Opportunity created',
        isEditMode ? `${oppName} has been updated` : `${oppName} has been created`
      );
      onClose();
    },
    onError: (error) => {
      console.error('[OpportunityModal] Save error:', error);
      showError('Error', 'Failed to save opportunity. Please try again.');
    }
  });

  const handleSubmit = (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    console.log('[OpportunityModal] handleSubmit called with formData:', formData);

    if (!validate()) {
      console.log('[OpportunityModal] Validation failed');
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }
    console.log('[OpportunityModal] Validation passed, proceeding with save');

    console.log('[OpportunityModal] Calling saveMutation.mutate with:', { ...formData, notes });
    saveMutation.mutate({ ...formData, notes });
  };

  // Format currency for display
  const formatCurrency = (value: string) => {
    if (!value) return '';
    const num = Number(value);
    if (isNaN(num)) return value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(num);
  };

  return (
    <TabbedModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Opportunity' : 'New Opportunity'}
      tabs={tabs}
      maxWidth="2xl"
      actions={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#334155] rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending
              ? 'Saving...'
              : isEditMode ? 'Save Changes' : 'Create Opportunity'
            }
          </button>
        </div>
      }
    >
      {(activeTab) => (
        <form onSubmit={handleSubmit} className="space-y-6">
          {activeTab === 'overview' && (
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              {loadingLayout ? (
                // Loading skeleton
                <div className="space-y-6">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i}>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : pageLayout?.sections && pageLayout.sections.length > 0 ? (
                // Dynamic sections from Object Manager
                pageLayout.sections.map(section => (
                  <EditSection
                    key={section.id}
                    section={section}
                    objectApiName="Opportunity__c"
                    formData={formData}
                    onChange={handleFieldChange}
                    errors={errors}
                    disabled={saveMutation.isPending}
                  />
                ))
              ) : (
                // No sections configured
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No page layout configured for Opportunity.</p>
                  <p className="text-sm mt-2">
                    Go to Object Manager to configure page layouts.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'related' && opportunity?.id && (
            <RelatedRecordsTab
              recordId={opportunity.id}
              objectType="Opportunity__c"
              recordName={(opportunity as any).Name__c as string}
            />
          )}

          {activeTab === 'pipeline' && opportunity?.id && (
            <StageProgressionTab
              opportunityId={opportunity.id}
              currentStage={formData.Stage__c}
              onStageChange={async (newStage) => {
                // PE-1470: Get the auto-fill configuration for this stage
                const stageValues = getStructuredPicklistValues('Stage__c', 'obj_opportunity');
                const selectedStage = stageValues?.find(pv => pv.value === newStage);
                const targetField = selectedStage?.metadata?.targetField as string | undefined;
                const defaultValue = selectedStage?.metadata?.defaultValue;

                const updates: Record<string, any> = { Stage__c: newStage };
                if (targetField && defaultValue !== undefined) {
                  updates[targetField] = defaultValue;
                }

                setFormData(prev => ({
                  ...prev,
                  Stage__c: newStage,
                  ...(targetField && defaultValue !== undefined ? { [targetField]: defaultValue.toString() } : {})
                }));

                try {
                  await updateRecord('Opportunity__c', String(opportunity.id), updates, { gobodoPropertyPatch: true });
                  queryClient.invalidateQueries({ queryKey: ['opportunityRecords'] });
                  queryClient.invalidateQueries({ queryKey: ['opportunityListViewData'] });
                  queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Opportunity__c'] });
                } catch (error) {
                  console.error('[OpportunityModal] Failed to update stage:', error);
                  showError('Error', 'Failed to update opportunity stage');
                }
              }}
            />
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              {/* Add Note */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Add Note
                </label>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Type your note here..."
                  rows={4}
                  className="w-full px-3 py-2 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddNote}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Note
                </button>
              </div>

              {/* Existing Notes */}
              {notes.length > 0 ? (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Notes ({notes.length})
                  </label>
                  <div className="space-y-2">
                    {notes.map((note, index) => (
                      <div
                        key={index}
                        className="p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-gray-700 rounded-lg"
                      >
                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{note}</p>
                        <button
                          type="button"
                          onClick={() => handleRemoveNote(index)}
                          className="text-sm text-red-600 dark:text-red-400 hover:underline mt-2"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No notes yet. Add your first note above.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && opportunity?.id && (
            <ActivityTab
              recordId={opportunity.id}
              objectType="Opportunity__c"
              recordName={(opportunity as any).Name__c as string}
            />
          )}
        </form>
      )}
    </TabbedModal>
  );
};
